// pages/players/PlayerDetailsPage.jsx
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PlayerDetailsTab from "./tabs/DetailsTab";
import PlayerTeamsTab from "./tabs/TeamsTab";
import PlayerTournamentsTab from "./tabs/TournamentsTab";
import LoadingSpinner from "../../utils/general/LoadingSpinner";
import { ArrowLeft, User } from "lucide-react";

const PlayerDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [player, setPlayer] = useState(null);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("details");

  useEffect(() => {
    const fetchPlayerDetails = async () => {
      try {
        const response = await fetch(
          `https://server-postgre.vercel.app/api/players/${id}`,
          { method: "GET" }
        );
        if (!response.ok)
          throw new Error("Error al obtener detalles del jugador.");

        const data = await response.json();
        setPlayer(data);
      } catch (err) {
        setError("Error al obtener detalles del jugador.");
        console.error(err);
      }
    };

    fetchPlayerDetails();
  }, [id]);

  if (error) return <p className="text-red-500">{error}</p>;
  if (!player) return <LoadingSpinner />;

  return (
    <div className="bg-gray-50 min-h-screen font-sans p-4">
      <div className="max-w-2xl mx-auto p-4">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
          body { font-family: 'Inter', sans-serif; }
        `}</style>

        {/* Header del jugador */}
        <div className="flex items-center gap-4 p-4 rounded-3xl bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-md mb-6">
          <button
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/20 p-2 rounded-full transition"
          >
            <ArrowLeft size={22} strokeWidth={2.5} />
          </button>

          {/* Avatar: foto o ícono circular */}
          {player.profile_picture_url ? (
            <img
              src={player.profile_picture_url}
              alt={`${player.first_name} ${player.last_name}`}
              className="w-16 h-16 rounded-full object-cover border-2 border-white/30"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center border-2 border-white/30">
              <User size={32} />
            </div>
          )}

          <div>
            <h1 className="text-xl font-bold">
              {player.first_name || "Nombre del Jugador"}{" "}
              {player.last_name || ""}
            </h1>
            <p className="text-sm opacity-90">Jugador</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex justify-around bg-white rounded-2xl shadow-md p-2 mb-4">
          {["details", "teams", "tournaments"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-xl font-semibold transition-colors ${
                activeTab === tab
                  ? "bg-purple-500 text-white shadow"
                  : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              {tab === "details" && "Detalles"}
              {tab === "teams" && "Equipos"}
              {tab === "tournaments" && "Torneos"}
            </button>
          ))}
        </div>

        {/* Contenido del tab */}
        <div className="bg-white rounded-3xl shadow p-4">
          {activeTab === "details" && <PlayerDetailsTab player={player} />}
          {activeTab === "teams" && <PlayerTeamsTab player={player} />}
          {activeTab === "tournaments" && (
            <PlayerTournamentsTab player={player} />
          )}
        </div>
      </div>
    </div>
  );
};

export default PlayerDetailsPage;
